import { RowCrudStatus } from "../../common/types";
import {
  Phonogram,
  Phonogram_CreateInput,
  Phonogram_UpdateInput,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { interpreterDisplayId } from "../../interpreter/store/InterpreterCommonStore";
import { blankToNull } from "../../../common/blankToNull";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import {
  getFileVariableValue,
  getNotNullFileVariableValue,
} from "../../common/utils";
import {
  MusicalWorkLinkValuesType,
  getMusicalWorkLinkValues,
  getMusicalWorkLinkCreateInput,
  getMusicalWorkLinkUpdateInput,
} from "../../musicalWorkLink/store/MusicalWorkLinkCommonStore";
import {
  addedStatusList,
  originalDeletedStatusList,
  originalUpdatedStatusList,
} from "../../common/types";

let uniqueCounter = 1;
export type PhonogramValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  trackName: string;
  __performers: string[];
  showPerformers: { displayId: string; id: string }[];
  albumName: string;
  publicationYear: number | undefined;
  isrc: string;
  originalProducer: string;
  audioFile: string;
  audioFile_data: FileCRUDType;
  __audioFile_files: FileCRUDType[];
  mainAudio: boolean;
  showLinks: MusicalWorkLinkValuesType[];
  notes: string;
};

export type PhonogramErrorsType = {
  trackName?: { message: string; fieldLabel: string };
  performers?: { message: string; fieldLabel: string };
  albumName?: { message: string; fieldLabel: string };
  publicationYear?: { message: string; fieldLabel: string };
  isrc?: { message: string; fieldLabel: string };
  originalProducer?: { message: string; fieldLabel: string };
  audioFile?: { message: string; fieldLabel: string };
  mainAudio?: { message: string; fieldLabel: string };
  links?: { message: string; fieldLabel: string };
  notes?: { message: string; fieldLabel: string };
};

export function PhonogramValuesInit(): PhonogramValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    trackName: "",
    __performers: [],
    showPerformers: [],
    albumName: "",
    publicationYear: undefined,
    isrc: "",
    originalProducer: "",
    audioFile: "",
    audioFile_data: fileCRUDInit(),
    __audioFile_files: [],
    mainAudio: false,
    showLinks: [],
    notes: "",
  };
}

export type PhonogramType = Phonogram | PhonogramValuesType;

export function phonogramDisplayId(
  source: Partial<Phonogram>,
  _langStore: LangStore
) {
  return source.trackName ?? _langStore.emptyStringSymbol;
}

export function getPhonogramValues(
  source: PhonogramType,
  _langStore: LangStore,
  _duplicated: boolean = false
): PhonogramValuesType {
  const entityValues = PhonogramValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : phonogramDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.trackName = source.trackName;
  entityValues.showPerformers =
    "performers" in source && source.performers
      ? (source.performers || []).map((x) => {
          return { id: x.id, displayId: interpreterDisplayId(x, _langStore) };
        })
      : "showPerformers" in source && source.showPerformers
      ? (source.showPerformers || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__performers = entityValues.showPerformers.map(
    (x) => x.displayId
  );
  entityValues.albumName = source.albumName || "";
  entityValues.publicationYear =
    (source.publicationYear as unknown) === "" ||
    source.publicationYear === undefined ||
    source.publicationYear === null
      ? undefined
      : source.publicationYear;
  entityValues.isrc = source.isrc || "";
  entityValues.originalProducer = source.originalProducer || "";
  entityValues.__audioFile_files =
    source.audioFile_data && !_duplicated
      ? [
          {
            id:
              "id" in source.audioFile_data && source.audioFile_data.id
                ? source.audioFile_data.id
                : `generated_${uniqueCounter++}`,
            name: source.audioFile_data.name,
            description: source.audioFile_data.description || undefined,
            size: source.audioFile_data.size || 0,
            formattedSize: source.audioFile_data.size
              ? formatBytes(source.audioFile_data.size)
              : "",
            mimetype: source.audioFile_data.mimetype,
            url: source.audioFile_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.audioFile =
    source.audioFile && !_duplicated ? source.audioFile : "";
  entityValues.audioFile_data =
    source.audioFile_data && "__status__" in source.audioFile_data
      ? source.audioFile_data
      : source.audioFile_data
      ? {
          id: ("id" in source.audioFile_data && source.audioFile_data.id
            ? source.audioFile_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.audioFile_data.name,
          description: source.audioFile_data.description || undefined,
          size: source.audioFile_data.size || 0,
          formattedSize: source.audioFile_data.size
            ? formatBytes(source.audioFile_data.size)
            : "",
          mimetype: source.audioFile_data.mimetype,
          url: source.audioFile_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.mainAudio = source.mainAudio || false;
  entityValues.showLinks =
    "links" in source && source.links
      ? (source.links || []).map((x) => {
          const row: MusicalWorkLinkValuesType = getMusicalWorkLinkValues(
            x,
            _langStore,
            _duplicated
          );
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showLinks" in source && source.showLinks
      ? (source.showLinks || []).map((x) => {
          const row: MusicalWorkLinkValuesType = getMusicalWorkLinkValues(
            x,
            _langStore,
            _duplicated
          );
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.notes = source.notes || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getPhonogramUpdateInput(
  values: PhonogramValuesType
): Phonogram_UpdateInput {
  return {
    id: values.id,
    version_: values.version_,
    trackName: values.trackName,
    performersIds: values.showPerformers
      ? { set: values.showPerformers.map((x) => x.id) }
      : undefined,
    albumName: blankToNull(values.albumName),
    publicationYear:
      values.publicationYear === undefined ? null : values.publicationYear,
    isrc: blankToNull(values.isrc),
    originalProducer: blankToNull(values.originalProducer),
    audioFile: getFileVariableValue(values.__audioFile_files),
    mainAudio: values.mainAudio,
    links: {
      create: values.showLinks
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getMusicalWorkLinkCreateInput(x)),
      update: values.showLinks
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getMusicalWorkLinkUpdateInput(x)),
      remove: values.showLinks
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
    notes: blankToNull(values.notes),
  };
}

export function getPhonogramCreateInput(
  values: PhonogramValuesType
): Phonogram_CreateInput {
  return {
    trackName: values.trackName,
    performersIds: values.showPerformers
      ? values.showPerformers.map((x) => x.id)
      : undefined,
    albumName: blankToNull(values.albumName),
    publicationYear:
      values.publicationYear === undefined ? null : values.publicationYear,
    isrc: blankToNull(values.isrc),
    originalProducer: blankToNull(values.originalProducer),
    audioFile: getNotNullFileVariableValue(values.__audioFile_files),
    mainAudio: values.mainAudio,
    links: values.showLinks
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getMusicalWorkLinkCreateInput(x)),
    notes: blankToNull(values.notes),
  };
}
